import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsApi, fileApi } from '../../api/endpoints.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import Card from '../../components/Card.jsx';
import Badge from '../../components/Badge.jsx';
import Spinner from '../../components/Spinner.jsx';
import { Field, Select, TextInput, TextArea } from '../../components/Field.jsx';
import { LOCALES } from '../../i18n/index.js';
import { useBranding } from '../../hooks/useBranding.jsx';

const INTEGRATIONS = ['telephony', 'mailbox', 'calendar', 'invoicing', 'agent', 'webhooks'];

/**
 * Real settings, not a mockup: what is saved here is what the API reads at
 * runtime. `.env` provides the defaults, this screen overrides them, and Reset
 * drops back to the file.
 */
const SettingsPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const toMessage = useApiError();
  const { refresh: refreshBranding } = useBranding();

  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    settingsApi
      .get()
      .then((data) => {
        setSettings(data);
        setDirty(false);
      })
      .catch((err) => toast.error(toMessage(err)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) return <Spinner full />;

  const patch = (part) => {
    setSettings((current) => ({ ...current, ...part }));
    setDirty(true);
  };

  const patchCompany = (part) => {
    setSettings((current) => ({ ...current, company: { ...current.company, ...part } }));
    setDirty(true);
  };

  /**
   * Uploading a logo SAVES it straight away.
   *
   * It used to only stage the id in the form, so choosing a file and leaving the
   * page silently did nothing - the file was stored but nothing pointed at it.
   * A picture is a decision, not a draft: persist it and refresh the header.
   */
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await fileApi.upload(file);
      const saved = await settingsApi.update({ company: { ...settings.company, logoFileId: uploaded.id } });
      setSettings(saved);
      await refreshBranding();
      toast.success(t('settings.logoSaved'));
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setUploading(false);
    }
  };

  /** Same for removing it. */
  const removeLogo = async () => {
    try {
      const saved = await settingsApi.update({ company: { ...settings.company, logoFileId: null } });
      setSettings(saved);
      await refreshBranding();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const patchIntegration = (key, value) => {
    setSettings((current) => ({ ...current, integrations: { ...current.integrations, [key]: value } }));
    setDirty(true);
  };

  const toggleLocale = (code) => {
    const next = settings.locales.includes(code)
      ? settings.locales.filter((item) => item !== code)
      : [...settings.locales, code];
    if (!next.length) return; // never leave the app with zero languages
    patch({ locales: next });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { id, ...body } = settings;
      const saved = await settingsApi.update(body);
      setSettings(saved);
      setDirty(false);
      await refreshBranding();
      toast.success(t('common.save'));
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const saved = await settingsApi.reset();
      setSettings(saved);
      setDirty(false);
      toast.success(t('settings.resetDone'));
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('settings.title')}
        subtitle={dirty ? t('settings.unsaved') : t('settings.savedHint')}
        actions={
          <div className="header-actions">
            <button type="button" className="btn" onClick={reset} disabled={busy}>
              {t('settings.reset')}
            </button>
            <button type="button" className="btn btn--primary" onClick={save} disabled={busy || !dirty}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </div>
        }
      />

      <div className="grid grid--2">
        <Card title={t('settings.localization')}>
          <Field label={t('settings.defaultLocale')} htmlFor="s-locale">
            <Select
              id="s-locale"
              value={settings.defaultLocale}
              onChange={(value) => patch({ defaultLocale: value })}
              options={LOCALES.map((locale) => ({ value: locale.code, label: locale.nativeLabel }))}
            />
          </Field>

          <p className="field__label">{t('settings.enabledLocales')}</p>
          <div className="options options--checkbox">
            {LOCALES.map((locale) => (
              <label
                key={locale.code}
                className={settings.locales.includes(locale.code) ? 'option option--checked' : 'option'}
              >
                <input
                  type="checkbox"
                  checked={settings.locales.includes(locale.code)}
                  onChange={() => toggleLocale(locale.code)}
                />
                <span>
                  {locale.flag} {locale.nativeLabel}
                </span>
              </label>
            ))}
          </div>

          <Field label={t('settings.currency')} htmlFor="s-currency">
            <Select
              id="s-currency"
              value={settings.currency}
              onChange={(value) => patch({ currency: value })}
              options={['EUR', 'USD', 'ILS', 'GBP', 'CHF'].map((code) => ({ value: code, label: code }))}
            />
          </Field>
        </Card>

        <Card title={t('it.accessRule')}>
          <div className="setting-row">
            <div>
              <p className="setting-row__title">
                {settings.itFullCustomerAccess ? t('it.accessRuleAll') : t('it.accessRuleScoped')}
              </p>
              <p className="muted small">{t('settings.itRuleHint')}</p>
            </div>
            <label className={settings.itFullCustomerAccess ? 'switch switch--on' : 'switch'}>
              <input
                type="checkbox"
                checked={Boolean(settings.itFullCustomerAccess)}
                onChange={(event) => patch({ itFullCustomerAccess: event.target.checked })}
              />
              <span className="switch__track" aria-hidden="true" />
            </label>
          </div>
        </Card>

        <Card title={t('settings.agenda')}>
          <div className="form-grid">
            <Field label={t('settings.defaultDuration')} htmlFor="s-duration">
              <Select
                id="s-duration"
                value={String(settings.defaultAppointmentMinutes)}
                onChange={(value) => patch({ defaultAppointmentMinutes: Number(value) })}
                options={[30, 45, 60, 90, 120].map((m) => ({ value: String(m), label: `${m} ${t('common.minutes')}` }))}
              />
            </Field>
            <Field label={t('settings.slotGranularity')} htmlFor="s-granularity">
              <Select
                id="s-granularity"
                value={String(settings.slotGranularityMinutes)}
                onChange={(value) => patch({ slotGranularityMinutes: Number(value) })}
                options={[15, 30, 60].map((m) => ({ value: String(m), label: `${m} ${t('common.minutes')}` }))}
              />
            </Field>
            <Field label={t('settings.workdayStart')} htmlFor="s-start">
              <TextInput
                id="s-start"
                type="time"
                value={settings.workdayStart}
                onChange={(value) => patch({ workdayStart: value })}
              />
            </Field>
            <Field label={t('settings.workdayEnd')} htmlFor="s-end">
              <TextInput
                id="s-end"
                type="time"
                value={settings.workdayEnd}
                onChange={(value) => patch({ workdayEnd: value })}
              />
            </Field>
          </div>
        </Card>

        <Card title={t('settings.scoring')}>
          <div className="form-grid">
            <Field label={t('settings.hotThreshold')} htmlFor="s-hot" hint={t('settings.thresholdHint')}>
              <TextInput
                id="s-hot"
                type="number"
                value={settings.leadHotThreshold}
                onChange={(value) => patch({ leadHotThreshold: Number(value) })}
              />
            </Field>
            <Field label={t('settings.warmThreshold')} htmlFor="s-warm">
              <TextInput
                id="s-warm"
                type="number"
                value={settings.leadWarmThreshold}
                onChange={(value) => patch({ leadWarmThreshold: Number(value) })}
              />
            </Field>
          </div>
          <div className="scale-preview">
            <Badge value="COLD" label={`${t('leads.temperatures.COLD')} < ${settings.leadWarmThreshold}`} />
            <Badge
              value="WARM"
              label={`${t('leads.temperatures.WARM')} ${settings.leadWarmThreshold}-${settings.leadHotThreshold - 1}`}
            />
            <Badge value="HOT" label={`${t('leads.temperatures.HOT')} >= ${settings.leadHotThreshold}`} />
          </div>
        </Card>
      </div>

      {/* Everything printed on a quote, invoice or receipt - and the CRM header. */}
      <Card title={t('settings.company')} actions={<span className="muted small">{t('settings.companyHint')}</span>}>
        <div className="logo-row">
          <div className="logo-preview">
            {settings.company?.logoFileId ? (
              <img src={fileApi.url(settings.company.logoFileId)} alt={settings.company?.name ?? ''} />
            ) : (
              <span className="logo-preview__empty">{t('settings.noLogo')}</span>
            )}
          </div>
          <div className="logo-actions">
            <label className="btn btn--small">
              {uploading ? t('settings.uploading') : t('settings.uploadLogo')}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                hidden
                onChange={(event) => uploadLogo(event.target.files?.[0])}
              />
            </label>
            {settings.company?.logoFileId ? (
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={removeLogo}
              >
                {t('common.delete')}
              </button>
            ) : null}
            <p className="muted small">{t('settings.logoHint')}</p>
          </div>
        </div>

        <div className="form-grid">
          <Field label={t('settings.companyName')} htmlFor="co-name" required>
            <TextInput
              id="co-name"
              value={settings.company?.name ?? ''}
              onChange={(v) => patchCompany({ name: v })}
            />
          </Field>
          <Field label={t('settings.legalForm')} htmlFor="co-form">
            <TextInput
              id="co-form"
              value={settings.company?.legalForm ?? ''}
              onChange={(v) => patchCompany({ legalForm: v })}
            />
          </Field>
          <div className="span-2">
            <Field label={t('customers.address')} htmlFor="co-address">
              <TextInput
                id="co-address"
                value={settings.company?.address ?? ''}
                onChange={(v) => patchCompany({ address: v })}
              />
            </Field>
          </div>
          <Field label={t('settings.postalCode')} htmlFor="co-zip">
            <TextInput
              id="co-zip"
              value={settings.company?.postalCode ?? ''}
              onChange={(v) => patchCompany({ postalCode: v })}
            />
          </Field>
          <Field label={t('customers.city')} htmlFor="co-city">
            <TextInput id="co-city" value={settings.company?.city ?? ''} onChange={(v) => patchCompany({ city: v })} />
          </Field>
          <Field label={t('customers.country')} htmlFor="co-country">
            <TextInput
              id="co-country"
              value={settings.company?.country ?? ''}
              onChange={(v) => patchCompany({ country: v })}
            />
          </Field>
          <Field label={t('customers.phone')} htmlFor="co-phone">
            <TextInput id="co-phone" value={settings.company?.phone ?? ''} onChange={(v) => patchCompany({ phone: v })} />
          </Field>
          <Field label={t('customers.email')} htmlFor="co-email">
            <TextInput id="co-email" value={settings.company?.email ?? ''} onChange={(v) => patchCompany({ email: v })} />
          </Field>
          <Field label={t('customers.website')} htmlFor="co-web">
            <TextInput id="co-web" value={settings.company?.website ?? ''} onChange={(v) => patchCompany({ website: v })} />
          </Field>
          <Field label={t('settings.vatNumber')} htmlFor="co-vat">
            <TextInput
              id="co-vat"
              value={settings.company?.vatNumber ?? ''}
              onChange={(v) => patchCompany({ vatNumber: v })}
            />
          </Field>
          <Field label={t('settings.registrationNumber')} htmlFor="co-reg">
            <TextInput
              id="co-reg"
              value={settings.company?.registrationNumber ?? ''}
              onChange={(v) => patchCompany({ registrationNumber: v })}
            />
          </Field>
          <Field label="IBAN" htmlFor="co-iban">
            <TextInput id="co-iban" value={settings.company?.iban ?? ''} onChange={(v) => patchCompany({ iban: v })} />
          </Field>
          <Field label="BIC" htmlFor="co-bic">
            <TextInput id="co-bic" value={settings.company?.bic ?? ''} onChange={(v) => patchCompany({ bic: v })} />
          </Field>
          <Field label={t('settings.accentColor')} htmlFor="co-accent" hint={t('settings.accentHint')}>
            <TextInput
              id="co-accent"
              type="color"
              value={settings.company?.accentColor ?? '#2f5bea'}
              onChange={(v) => patchCompany({ accentColor: v })}
            />
          </Field>
          <Field label={t('settings.paymentTerms')} htmlFor="co-terms-days">
            <TextInput
              id="co-terms-days"
              type="number"
              value={settings.company?.paymentTermsDays ?? 30}
              onChange={(v) => patchCompany({ paymentTermsDays: Number(v) })}
            />
          </Field>
          <Field label={t('settings.defaultVat')} htmlFor="co-vat-rate">
            <TextInput
              id="co-vat-rate"
              type="number"
              value={settings.company?.defaultVatRate ?? 20}
              onChange={(v) => patchCompany({ defaultVatRate: Number(v) })}
            />
          </Field>
          <div className="span-2">
            <Field label={t('documents.terms')} htmlFor="co-terms">
              <TextArea
                id="co-terms"
                value={settings.company?.defaultTerms ?? ''}
                onChange={(v) => patchCompany({ defaultTerms: v })}
              />
            </Field>
          </div>
          <div className="span-2">
            <Field label={t('settings.legalMentions')} htmlFor="co-legal">
              <TextArea
                id="co-legal"
                value={settings.company?.legalMentions ?? ''}
                onChange={(v) => patchCompany({ legalMentions: v })}
              />
            </Field>
          </div>
        </div>
      </Card>

      <Card title={t('it.integrations')} actions={<span className="muted small">{t('settings.integrationsHint')}</span>}>
        <div className="grid grid--3">
          {INTEGRATIONS.map((key) => (
            <div key={key} className="setting-row">
              <div>
                <p className="setting-row__title">{t(`integrations.${key}`)}</p>
                <p className="muted small">{t(`integrations.${key}Hint`)}</p>
              </div>
              <label className={settings.integrations?.[key] ? 'switch switch--on' : 'switch'}>
                <input
                  type="checkbox"
                  checked={Boolean(settings.integrations?.[key])}
                  onChange={(event) => patchIntegration(key, event.target.checked)}
                />
                <span className="switch__track" aria-hidden="true" />
              </label>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};

export default SettingsPage;
